import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import {
  Plus,
  Search,
  List,
  Calendar as CalendarIcon,
  BarChart3,
  Users,
  MapPin,
  Clock,
  Eye,
  History,
  Filter,
  X,
  ArrowUp,
  TrendingUp,
  CheckCircle2,
  Plane,
  XCircle,
  AlertCircle,
  ReceiptText,
  House,
  UserRound,
  CarFront,
  Compass,
  Download,
  UserRoundPlus,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";
import { getWalkInTours } from "services/tourWalkinService";

interface WalkinTourListPageEnhancedProps {
  onNavigate?: (page: string, tourId?: string) => void;
}

interface VenueSchedule {
  venueName: string;
  checkInDate: string;
  checkOutDate: string;
  checkInTime: string;
  checkOutTime: string;
}

interface Tour {
  id: string;
  publicId: string;
  tourCode: string;
  tourName: string;

  tourType?: number;
  tourCategory?: number | null;
  faizAccommodation?: boolean;

  accommodationPreference?: number;
  specialAccommodationRequest?: string;

  rotationName?: string;
  miqaatName?: string;

  arrivalDate: string;
  departureDate: string;
  durationDays: number;

  totalApplicants: number;
  venueCount: number;

  venues: VenueSchedule[];

  entries: {
    publicId: string;
    lawazimTypeName: string;
    description: string;
    amount: number;
  }[];

  isArrivalConfirmed: boolean;
  isDepartureConfirmed: boolean;

  status: number;
  statusName: string;

  arrivalPort: string;
  departurePort: string;

  accommodationStatus?: "R" | "Y" | "O" | "G";
  transportStatus?: "R" | "Y" | "O" | "G";
  paxStatus?: "R" | "Y" | "O" | "G";
  itineraryStatus?: "R" | "Y" | "O" | "G";

  venueApprovalStatus?: "pending" | "approved" | "declined" | "date-changed";
}

type WorkflowStatus = "R" | "Y" | "O" | "G";

type ActivityLog = {
  process: string;
  status: string;
  details: string;
  dateTime: string;
  doneBy: string;
};

const workflowStatusClass = (status?: WorkflowStatus) => {
  switch (status) {
    case "Y":
      return "border-yellow-300 bg-yellow-100 text-yellow-700";

    case "O":
      return "border-orange-300 bg-orange-100 text-orange-700";

    case "G":
      return "border-green-300 bg-green-100 text-green-700";

    default:
      return "border-red-300 bg-red-100 text-red-700";
  }
};

const workflowStatusLabel = (status?: WorkflowStatus) => {
  switch (status) {
    case "Y":
      return "Requested";

    case "O":
      return "Partial";

    case "G":
      return "Completed";

    default:
      return "Not Started";
  }
};

export type MiqaatSelection = {
  type: "general" | "package";
  packageId?: string;
  packageName?: string;
  rotationId?: number;
  arrivalDate?: string;
  departureDate?: string;
};

const getStatusBadgeClass = (status: number) => {
  switch (status) {
    case 1: // Draft
      return "bg-gray-100 text-gray-700 border-gray-300";

    case 2: // Submitted
      return "bg-blue-100 text-blue-700 border-blue-300";

    case 3: // Alternative Dates Proposed
      return "bg-orange-100 text-orange-700 border-orange-300";

    case 4: // Resubmitted
      return "bg-indigo-100 text-indigo-700 border-indigo-300";

    case 5: // Confirmed
      return "bg-green-100 text-green-700 border-green-300";

    case 6: // Cancelled
      return "bg-red-100 text-red-700 border-red-300";

    case 7: // Checked In
      return "bg-cyan-100 text-cyan-700 border-cyan-300";

    case 8: // Checked Out
      return "bg-purple-100 text-purple-700 border-purple-300";

    case 9: // Completed
      return "bg-emerald-100 text-emerald-700 border-emerald-300";

    default:
      return "bg-gray-100 text-gray-700 border-gray-300";
  }
};

export default function WalkinTourListPageEnhanced({
  onNavigate,
}: WalkinTourListPageEnhancedProps) {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<"list" | "calendar" | "analytics">(
    "list",
  );
  const isViewMode = (mode: "list" | "calendar" | "analytics") =>
    viewMode === mode;
  const [showStatistics, setShowStatistics] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [tours, setTours] = useState<Tour[]>([]);
  const [, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const loadWalkInTours = async () => {
      try {
        setLoading(true);

        const response = await getWalkInTours(1, 1000);

        const mappedTours: Tour[] = response.items.map((tour) => ({
          id: String(tour.tourId),
          publicId: tour.publicId,
          tourCode: tour.tourCode,
          tourName: tour.tourName,

          tourType: tour.tourType,
          tourCategory: tour.tourCategory,
          faizAccommodation: tour.faizAccommodation,

          accommodationPreference: tour.accommodationPreference ?? undefined,

          specialAccommodationRequest:
            tour.specialAccommodationRequest ?? undefined,

          rotationName: tour.rotationName ?? undefined,
          miqaatName: tour.rotationName ?? undefined,

          arrivalDate: tour.arrivalDate,
          departureDate: tour.departureDate,
          durationDays: tour.durationDays,

          totalApplicants: tour.noOfApplicants,
          venueCount: tour.venueCount,

          venues: tour.venueSchedules.map((venue) => ({
            venueName: venue.venueName,
            checkInDate: venue.checkInDate,
            checkOutDate: venue.checkOutDate,
            checkInTime: venue.checkInTime,
            checkOutTime: venue.checkOutTime,
          })),

          entries: [],

          isArrivalConfirmed: tour.isArrivalConfirmed,
          isDepartureConfirmed: tour.isDepartureConfirmed,

          status: tour.status,
          statusName: tour.statusName,

          // The list API response does not currently contain port names.
          arrivalPort: "",
          departurePort: "",

          accommodationStatus: tour.accommodationStatus,
          transportStatus: tour.transportStatus,
          paxStatus: tour.paxStatus,
          itineraryStatus: tour.itineraryStatus,

          // The list response does not currently provide a venue approval
          // status matching this UI field.
          venueApprovalStatus: "approved",
        }));

        setTours(mappedTours);
      } catch (error) {
        console.error("Failed to load walk-in tours:", error);
        setTours([]);
      } finally {
        setLoading(false);
      }
    };

    loadWalkInTours();
  }, [pageNo, pageSize, location.key]);

  const [activityLogs] = useState<ActivityLog[]>([]);
  const [activityLogTour, setActivityLogTour] = useState<Tour | null>(null);
  const [receiptTour, setReceiptTour] = useState<Tour | null>(null);

  const getInitialBookingFilter = () => {
    const params = new URLSearchParams(window.location.search);

    const filter = params.get("bookingFilter");

    if (
      filter === "all" ||
      filter === "past" ||
      filter === "current" ||
      filter === "future"
    ) {
      return filter;
    }

    return "current";
  };

  const [bookingFilter, setBookingFilter] = useState<
    "all" | "past" | "current" | "future"
  >(getInitialBookingFilter);

  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  useEffect(() => {
    setPageHeader({
      title: "Walk-in Tours",
      icon: <UserRoundPlus className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [setPageHeader]);

  const [filters, setFilters] = useState({
    tourCode: "",
    miqaatName: "",
    status: "all",
    venueStatus: "all",
    month: "all",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const filter = params.get("bookingFilter");

    if (
      filter === "all" ||
      filter === "past" ||
      filter === "current" ||
      filter === "future"
    ) {
      setBookingFilter(filter);
    }
  }, [location.search]);

  useEffect(() => {
    console.log("URL Search:", location.search);
    console.log(
      "bookingFilter from URL:",
      new URLSearchParams(location.search).get("bookingFilter"),
    );
  }, [location.search]);

  useEffect(() => {
    console.log("bookingFilter state:", bookingFilter);
  }, [bookingFilter]);

  const handleCreateTourClick = async () => {
    try {
      setLoading(true);

      navigate("/walkin-tour/create");
    } finally {
      setLoading(false);
    }
  };

  const filteredTours = tours.filter((tour) => {
    const matchesSearch =
      tour.tourCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tour.tourName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tour.arrivalPort.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTourCode =
      !filters.tourCode ||
      tour.tourCode.toLowerCase().includes(filters.tourCode.toLowerCase());
    const matchesMiqaat =
      !filters.miqaatName ||
      tour.tourName.toLowerCase().includes(filters.miqaatName.toLowerCase());
    const matchesStatus =
      filters.status === "all" ||
      tour.statusName.toLowerCase() === filters.status.toLowerCase();
    const matchesVenueStatus =
      filters.venueStatus === "all" ||
      tour.venueApprovalStatus === filters.venueStatus;

    let matchesMonth = true;
    if (filters.month !== "all") {
      const tourMonth = new Date(tour.arrivalDate).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      matchesMonth = tourMonth === filters.month;
    }

    return (
      matchesSearch &&
      matchesTourCode &&
      matchesMiqaat &&
      matchesStatus &&
      matchesVenueStatus &&
      matchesMonth
    );
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatActivityLogDate = (date: string) => {
    const d = new Date(date);

    const datePart = d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const timePart = d
      .toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    return `${datePart}, ${timePart}`;
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);

  // Get unique months from tours
  const getUniqueMonths = () => {
    const months = new Set<string>();
    tours.forEach((tour) => {
      const month = new Date(tour.arrivalDate).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
      months.add(month);
    });
    return Array.from(months).sort();
  };

  // Calendar View Helper
  const getAllMonths = () => {
    if (tours.length === 0) return [];
    const minDate = new Date(
      Math.min(...tours.map((t) => new Date(t.arrivalDate).getTime())),
    );

    const start = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const end = new Date(2026, 11, 1);

    const months: Date[] = [];
    const current = new Date(start);

    while (current <= end) {
      months.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }

    return months;
  };

  const months = getAllMonths();
  const selectedMonth = months[selectedMonthIndex];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toursInSelectedMonth = filteredTours.filter((tour) => {
    const tourDate = new Date(tour.arrivalDate);

    return (
      tourDate.getMonth() === selectedMonth.getMonth() &&
      tourDate.getFullYear() === selectedMonth.getFullYear()
    );
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const hasToursInMonth = (month: Date) => {
    return filteredTours.some((tour) => {
      const tourDate = new Date(tour.arrivalDate);

      return (
        tourDate.getMonth() === month.getMonth() &&
        tourDate.getFullYear() === month.getFullYear()
      );
    });
  };

  // Analytics Data
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getAnalyticsData = () => {
    const monthlyData: {
      [key: string]: {
        month: string;
        tours: number;
        applicants: number;
        approved: number;
        pending: number;
        declined: number;
      };
    } = {};

    tours.forEach((tour) => {
      const month = new Date(tour.arrivalDate).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });

      if (!monthlyData[month]) {
        monthlyData[month] = {
          month,
          tours: 0,
          applicants: 0,
          approved: 0,
          pending: 0,
          declined: 0,
        };
      }

      monthlyData[month].tours += 1;
      monthlyData[month].applicants += tour.totalApplicants;

      if (tour.venueApprovalStatus === "approved")
        monthlyData[month].approved += 1;
      else if (tour.venueApprovalStatus === "pending")
        monthlyData[month].pending += 1;
      else if (tour.venueApprovalStatus === "declined")
        monthlyData[month].declined += 1;
    });

    return Object.values(monthlyData).sort((a, b) => {
      return new Date(a.month).getTime() - new Date(b.month).getTime();
    });
  };

  const getFilteredToursByDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return filteredTours.filter((tour) => {
      const start = new Date(tour.arrivalDate);
      const end = new Date(tour.departureDate);

      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (bookingFilter === "past") {
        return end < today;
      }

      if (bookingFilter === "current") {
        return start <= today && end >= today;
      }

      if (bookingFilter === "future") {
        return start > today;
      }

      return true;
    });
  };

  const dateFilteredTours = getFilteredToursByDate();

  const paginatedTours = dateFilteredTours.slice(
    (pageNo - 1) * pageSize,
    pageNo * pageSize,
  );

  const filteredTotalCount = dateFilteredTours.length;

  const bookingFilterLabel =
    bookingFilter === "past"
      ? "past"
      : bookingFilter === "current"
        ? "current"
        : bookingFilter === "future"
          ? "future"
          : "matching";

  const loadBookingReceipt = (tour: Tour) => {
    setReceiptTour(tour);
  };

  const handleDownloadBookingReceipt = (tour: Tour) => {
    console.log("Download booking receipt:", tour.tourCode);
  };

  return (
    <>
      <div className="space-y-6 pb-8">
        {/* Statistics Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total Tours</p>
                    <p className="text-2xl font-bold text-blue-900">0</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Approved</p>
                    <p className="text-2xl font-bold text-green-900">0</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 bg-orange-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-orange-600">Pending</p>
                    <p className="text-2xl font-bold text-orange-900">0</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Declined</p>
                    <p className="text-2xl font-bold text-red-900">0</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Total Applicants</p>
                    <p className="text-2xl font-bold text-purple-900">0</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search and Filter */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex justify-start">
            <div className="flex gap-2 bg-white border border-[#5B9BD5]/30 rounded-lg p-1 shadow-sm">
              <Button
                variant={isViewMode("list") ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                style={isViewMode("list") ? { background: "#5B9BD5" } : {}}
                className={isViewMode("list") ? "text-white" : "text-[#5B9BD5]"}
              >
                <List className="w-4 h-4 mr-2" />
                List
              </Button>
              <Button
                variant={isViewMode("calendar") ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("calendar")}
                style={isViewMode("calendar") ? { background: "#5B9BD5" } : {}}
                className={
                  isViewMode("calendar") ? "text-white" : "text-[#5B9BD5]"
                }
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                Calendar
              </Button>
              <Button
                variant={isViewMode("analytics") ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("analytics")}
                style={isViewMode("analytics") ? { background: "#5B9BD5" } : {}}
                className={
                  isViewMode("analytics") ? "text-white" : "text-[#5B9BD5]"
                }
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="relative flex items-center justify-end gap-2 overflow-visible pl-56 sm:pl-64">
              <div
                className={`absolute right-12 top-1/2 -translate-y-1/2 origin-right transition-all duration-300 ease-out ${
                  isSearchOpen
                    ? "w-52 sm:w-60 translate-x-0 opacity-100"
                    : "w-0 translate-x-4 opacity-0 pointer-events-none"
                }`}
              >
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search tours..."
                  value={searchTerm}
                  autoFocus={isSearchOpen}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPageNo(1);
                  }}
                  className="h-10 pl-9 pr-9 text-sm shadow-sm"
                />
                {(searchTerm || isSearchOpen) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (searchTerm) {
                        setSearchTerm("");
                        setPageNo(1);
                      } else {
                        setIsSearchOpen(false);
                      }
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title={searchTerm ? "Clear search" : "Close search"}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsSearchOpen((open) => !open)}
                className="border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50 shrink-0 bg-white"
                title="Search Tours"
              >
                <Search className="w-5 h-5" />
              </Button>
            </div>

            <Button
              onClick={handleCreateTourClick}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Walk-in Tour
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
              title="Toggle Statistics"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              style={showFilters ? { background: "#5B9BD5" } : {}}
              className={
                showFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }
              title="Toggle Advanced Filters"
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <Card
            className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px" }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Advanced Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter tours by multiple criteria
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Tour Code</Label>
                  <Input
                    placeholder="Search by tour code..."
                    value={filters.tourCode}
                    onChange={(e) =>
                      setFilters({ ...filters, tourCode: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Tour Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="under review">Under Review</SelectItem>
                      <SelectItem value="alternative dates proposed">
                        Alternative Dates Proposed
                      </SelectItem>
                      <SelectItem value="resubmitted">Resubmitted</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="checked in">Checked In</SelectItem>
                      <SelectItem value="checked out">Checked Out</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Month</Label>
                  <Select
                    value={filters.month}
                    onValueChange={(value) =>
                      setFilters({ ...filters, month: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      {getUniqueMonths().map((month) => (
                        <SelectItem key={month} value={month}>
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Summary */}
              {(filters.tourCode ||
                filters.miqaatName ||
                filters.status !== "all" ||
                filters.venueStatus !== "all" ||
                filters.month !== "all") && (
                <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#5B9BD5] mb-2">
                      Active Filters:
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {filters.tourCode && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Code: {filters.tourCode}
                        </Badge>
                      )}
                      {filters.miqaatName && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Miqaat: {filters.miqaatName}
                        </Badge>
                      )}
                      {filters.status !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Status: {filters.status}
                        </Badge>
                      )}
                      {filters.venueStatus !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Venue: {filters.venueStatus}
                        </Badge>
                      )}
                      {filters.month !== "all" && (
                        <Badge
                          variant="outline"
                          className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs"
                        >
                          Month: {filters.month}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilters({
                        tourCode: "",
                        miqaatName: "",
                        status: "all",
                        venueStatus: "all",
                        month: "all",
                      });
                    }}
                    className="text-red-600 border-red-300 hover:bg-red-50 flex-shrink-0"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* List View */}
        {isViewMode("list") && (
          <Card
            className="bg-white border-[#5B9BD5]/30"
            style={{ gap: "0px", marginTop: "10px" }}
          >
            <CardHeader
              className="border-b bg-gradient-to-r from-sky-50 to-blue-50"
              style={{
                paddingTop: "10px",
                paddingLeft: "16px",
                paddingRight: "16px",
              }}
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle style={{ color: "#5B9BD5" }}>
                    Walk-in Tour List
                  </CardTitle>
                  <CardDescription>
                    {dateFilteredTours.length} tour
                    {dateFilteredTours.length !== 1 ? "s" : ""} found
                  </CardDescription>
                </div>

                {/* RIGHT FILTER */}
                <div className="w-[200px]">
                  <Select
                    value={bookingFilter}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onValueChange={(val: any) => {
                      setBookingFilter(val);

                      navigate(`/walkin-tour?bookingFilter=${val}`, {
                        replace: true,
                      });
                    }}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="current">Current Bookings</SelectItem>
                      <SelectItem value="future">Future Bookings</SelectItem>
                      <SelectItem value="past">Past Bookings</SelectItem>
                      <SelectItem value="all">All Bookings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent style={{ padding: "0px 0px 10px 0px" }}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer hover:bg-sky-100"
                      >
                        Tour
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer hover:bg-sky-100"
                      >
                        Dates
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer hover:bg-sky-100"
                      >
                        Duration
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer hover:bg-sky-100"
                      >
                        Applicants
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs cursor-pointer hover:bg-sky-100"
                      >
                        Venue Status
                      </TableHead>
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedTours.map((tour) => (
                      <TableRow
                        key={tour.publicId}
                        className="hover:bg-sky-50/30"
                      >
                        <TableCell className="px-4 py-3">
                          <div className="space-y-0.5">
                            <div
                              className="font-mono font-semibold text-sm"
                              style={{ color: "#5B9BD5" }}
                            >
                              {tour.tourCode}
                            </div>
                            <div className="text-sm font-medium text-muted-foreground">
                              {tour.tourName}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="space-y-0.5">
                            {/* TOP: Full tour date */}
                            <div className="font-medium text-foreground">
                              {(() => {
                                const start = new Date(tour.arrivalDate);
                                const end = new Date(tour.departureDate);

                                const startDay = start.getDate();
                                const endDay = end.getDate();

                                const month = start.toLocaleDateString(
                                  "en-GB",
                                  {
                                    month: "short",
                                  },
                                );

                                return `${startDay}–${endDay} ${month}`;
                              })()}
                            </div>

                            {/* BOTTOM: Venue-wise dates */}
                            <div className="text-xs text-muted-foreground">
                              {(() => {
                                if (!tour.venues || tour.venues.length === 0)
                                  return "-";

                                // sort venues (important)
                                const sortedVenues = [...tour.venues].sort(
                                  (a, b) =>
                                    new Date(a.checkInDate).getTime() -
                                    new Date(b.checkInDate).getTime(),
                                );

                                return sortedVenues.map((v, i) => {
                                  const start = new Date(v.checkInDate);
                                  const end = new Date(v.checkOutDate);

                                  const startDay = start.getDate();
                                  const endDay = end.getDate();

                                  const month = start.toLocaleDateString(
                                    "en-GB",
                                    {
                                      month: "short",
                                    },
                                  );

                                  let label = "";
                                  const name = v.venueName?.toLowerCase() || "";

                                  if (name.includes("karbala")) label = "K";
                                  else if (name.includes("najaf")) label = "N";
                                  else if (name.includes("baghdad"))
                                    label = "B";
                                  else label = name.charAt(0).toUpperCase();

                                  return (
                                    <span key={i}>
                                      {label}: {startDay}–{endDay} {month}
                                      {i < sortedVenues.length - 1 && " • "}
                                    </span>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-gray-400" />
                            <span className="text-sm">
                              {tour.durationDays}D
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Users className="h-3 w-3 text-gray-400" />
                            <span className="font-semibold">
                              {tour.totalApplicants}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant="outline"
                              className="bg-amber-100 text-amber-700 border-amber-300"
                              style={{ width: "186px" }}
                            >
                              Walk-in Tour
                            </Badge>

                            <Badge
                              variant="outline"
                              className={getStatusBadgeClass(tour.status)}
                              style={{ width: "186px" }}
                            >
                              {tour.statusName}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {(() => {
                              // const isMiqaatTour =
                              //   Number(tour.tourType) === 2 ||
                              //   Number(tour.tourCategory) === 2;

                              const indicators = [
                                {
                                  key: "accommodation",
                                  label: "Accommodation",
                                  status: tour.accommodationStatus,
                                  Icon: House,
                                },
                                {
                                  key: "applicants",
                                  label: "Applicants",
                                  status: tour.paxStatus,
                                  Icon: UserRound,
                                },
                                // ...(!isMiqaatTour
                                //   ? [
                                {
                                  key: "transport",
                                  label: "Transport",
                                  status: tour.transportStatus,
                                  Icon: CarFront,
                                },
                                {
                                  key: "excursions",
                                  label: "Excursions",
                                  status: tour.itineraryStatus,
                                  Icon: Compass,
                                },
                                //   ]
                                // : []),
                              ];
                              return (
                                <div className="mr-1 flex items-center gap-1 border-r border-slate-200 pr-2">
                                  {indicators.map(
                                    ({ key, label, status, Icon }) => (
                                      <span
                                        key={key}
                                        title={
                                          label +
                                          ": " +
                                          workflowStatusLabel(status)
                                        }
                                        aria-label={
                                          label +
                                          ": " +
                                          workflowStatusLabel(status)
                                        }
                                        className={
                                          "inline-flex h-8 w-8 items-center justify-center rounded-lg border " +
                                          workflowStatusClass(status)
                                        }
                                      >
                                        <Icon className="h-4 w-4" />
                                      </span>
                                    ),
                                  )}
                                </div>
                              );
                            })()}
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => loadBookingReceipt(tour)}
                              className="h-8 w-8 hover:bg-sky-50 text-[#5B9BD5]"
                              title="Booking Receipt"
                            >
                              <ReceiptText className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 hover:bg-sky-50 text-[#5B9BD5]"
                              title="Activity Log"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                navigate(
                                  `/walkin-tour/tour-view-edit/${tour.publicId}?bookingFilter=${bookingFilter}`,
                                );
                              }}
                              className="h-8 w-8 hover:bg-sky-50 text-[#5B9BD5]"
                              title="View / Edit"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {dateFilteredTours.length === 0 && (
                <div className="text-center py-12">
                  <MapPin className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500">
                    No {bookingFilterLabel} tours found
                  </p>
                  <p className="text-sm text-gray-400">
                    Try adjusting your search or filters
                  </p>
                </div>
              )}
            </CardContent>
            {filteredTotalCount > 0 && (
              <Pagination
                totalItems={filteredTotalCount}
                itemsPerPage={pageSize}
                currentPage={pageNo}
                onPageChange={(page) => setPageNo(page)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPageNo(1);
                }}
                label="tours"
              />
            )}
          </Card>
        )}

        {/* Calendar View */}
        {isViewMode("calendar") &&
          (() => {
            // TODAY (normalized)
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // FILTER: remove past tours
            const upcomingTours = filteredTours.filter((tour) => {
              const end = new Date(tour.departureDate);
              end.setHours(0, 0, 0, 0);
              return end >= today;
            });

            // GROUP MONTHS (only from upcoming tours)
            const monthsMap = new Map<string, Date>();

            upcomingTours.forEach((tour) => {
              const d = new Date(tour.arrivalDate);
              const key = `${d.getFullYear()}-${d.getMonth()}`;

              if (!monthsMap.has(key)) {
                monthsMap.set(key, new Date(d.getFullYear(), d.getMonth(), 1));
              }
            });

            const months = Array.from(monthsMap.values()).sort(
              (a, b) => a.getTime() - b.getTime(),
            );

            const selectedMonth = months[selectedMonthIndex] || months[0];

            // FILTER tours in selected month
            const toursInSelectedMonth = upcomingTours.filter((tour) => {
              const d = new Date(tour.arrivalDate);
              return (
                d.getMonth() === selectedMonth?.getMonth() &&
                d.getFullYear() === selectedMonth?.getFullYear()
              );
            });

            return (
              <Card
                className="bg-white border-[#5B9BD5]/30"
                style={{ gap: "0px" }}
              >
                <CardHeader
                  className="border-b bg-gradient-to-r from-sky-50 to-blue-50"
                  style={{ padding: "20px", gap: "0px" }}
                >
                  <div className="flex items-center gap-4 overflow-hidden">
                    {/* LEFT TITLE */}
                    <div className="min-w-[120px]">
                      <CardTitle className="text-[#5B9BD5] whitespace-nowrap">
                        Select Month
                      </CardTitle>
                    </div>

                    {/* ONLY VALID MONTHS */}
                    <div className="flex-1 overflow-x-auto scrollbar-hide">
                      <div className="flex gap-2 w-max pr-4">
                        {months.map((month, index) => {
                          const isActive = selectedMonthIndex === index;

                          return (
                            <button
                              key={index}
                              onClick={() => setSelectedMonthIndex(index)}
                              className={`px-4 py-2 rounded-md text-sm whitespace-nowrap transition-all border flex-shrink-0
                        ${
                          isActive
                            ? "bg-[#5B9BD5] text-white border-[#5B9BD5]"
                            : "bg-gray-100 text-gray-600 border-gray-200 hover:border-[#5B9BD5] hover:text-[#5B9BD5]"
                        }
                      `}
                            >
                              {month.toLocaleDateString("en-US", {
                                month: "short",
                                year: "numeric",
                              })}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent style={{ padding: "20px" }}>
                  {toursInSelectedMonth.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                      No upcoming tours in this month
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {toursInSelectedMonth.map((tour) => (
                        <div
                          key={tour.publicId}
                          className="border-2 border-[#5B9BD5]/20 rounded-lg p-3 hover:shadow-lg transition-all cursor-pointer hover:border-[#5B9BD5] bg-white"
                          onClick={async () => {
                            try {
                              setLoading(true);

                              navigate(
                                `/tours/tour-view-edit/${tour.publicId}`,
                                {
                                  state: { tour },
                                },
                              );
                            } finally {
                              setLoading(false);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <Badge
                              style={{ background: "#5B9BD5" }}
                              className="text-xs text-white"
                            >
                              {tour.tourCode}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={`text-xs ${getStatusBadgeClass(tour.status)}`}
                            >
                              {tour.statusName}
                            </Badge>
                          </div>

                          <h4 className="font-semibold text-sm mb-2">
                            {tour.tourName}
                          </h4>

                          <div className="space-y-1 text-xs text-gray-600">
                            <div className="flex items-center gap-1">
                              <Plane className="h-3 w-3 text-green-600" />
                              <span>{formatDate(tour.arrivalDate)}</span>
                            </div>

                            <div className="flex items-center gap-1">
                              <Plane className="h-3 w-3 text-red-600 rotate-45" />
                              <span>{formatDate(tour.departureDate)}</span>
                            </div>

                            <div className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              <span>{tour.totalApplicants} applicants</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}

        {/* Analytics View */}
        {isViewMode("analytics") &&
          (() => {
            // TODAY
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // FILTER: remove past tours
            const upcomingTours = tours.filter((tour) => {
              const end = new Date(tour.departureDate);
              end.setHours(0, 0, 0, 0);
              return end >= today;
            });

            // BUILD ANALYTICS FROM FILTERED DATA
            const monthlyData: {
              [key: string]: {
                month: string;
                tours: number;
                applicants: number;
                approved: number;
                pending: number;
                declined: number;
              };
            } = {};

            upcomingTours.forEach((tour) => {
              const month = new Date(tour.arrivalDate).toLocaleDateString(
                "en-US",
                {
                  month: "short",
                  year: "numeric",
                },
              );

              if (!monthlyData[month]) {
                monthlyData[month] = {
                  month,
                  tours: 0,
                  applicants: 0,
                  approved: 0,
                  pending: 0,
                  declined: 0,
                };
              }

              monthlyData[month].tours += 1;
              monthlyData[month].applicants += tour.totalApplicants;

              if (tour.venueApprovalStatus === "approved")
                monthlyData[month].approved += 1;
              else if (tour.venueApprovalStatus === "pending")
                monthlyData[month].pending += 1;
              else if (tour.venueApprovalStatus === "declined")
                monthlyData[month].declined += 1;
            });

            const analyticsData = Object.values(monthlyData).sort(
              (a, b) =>
                new Date(a.month).getTime() - new Date(b.month).getTime(),
            );

            const maxApplicants = Math.max(
              ...analyticsData.map((d) => d.applicants),
              1, // prevent divide by 0
            );

            return (
              <Card
                className="bg-white border-[#5B9BD5]/30"
                style={{ gap: "0px" }}
              >
                <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50">
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2"
                  >
                    <TrendingUp className="w-5 h-5" />
                    Monthly Tour Analytics
                  </CardTitle>
                  <CardDescription className="pb-6">
                    Current & future bookings only (past excluded)
                  </CardDescription>
                </CardHeader>

                <CardContent className="p-6">
                  <div className="space-y-6">
                    {analyticsData.map((data, index) => {
                      const applicantsPercentage =
                        (data.applicants / maxApplicants) * 100;

                      return (
                        <div
                          key={index}
                          className="space-y-2 w-full max-w-full"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="min-w-[70px] sm:min-w-[90px]">
                                <p
                                  className="font-semibold"
                                  style={{ color: "#5B9BD5" }}
                                >
                                  {data.month}
                                </p>
                              </div>
                              <div className="flex gap-4 text-xs">
                                <span className="text-gray-600">
                                  <strong>{data.tours}</strong> tours
                                </span>
                                <span className="text-gray-600">
                                  <strong>{data.applicants}</strong> applicants
                                </span>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-1 sm:gap-2">
                              <Badge className="bg-green-100 text-green-700 border-green-300 text-xs whitespace-nowrap">
                                {data.approved} Approved
                              </Badge>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="relative h-9 sm:h-10 bg-gray-100 rounded-lg overflow-hidden">
                            <div
                              className="absolute top-0 left-0 h-full rounded-lg transition-all flex items-center"
                              style={{
                                width: `${applicantsPercentage}%`,
                                background:
                                  "linear-gradient(90deg, #5B9BD5 0%, #4A8CC7 100%)",
                              }}
                            >
                              {data.applicants > 0 && (
                                <span className="text-white text-xs sm:text-sm font-semibold px-2 truncate">
                                  {data.applicants}
                                  <span className="hidden sm:inline">
                                    {" "}
                                    applicants
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Breakdown */}
                          <div className="flex gap-1 h-2">
                            {data.approved > 0 && (
                              <div
                                className="bg-green-500 rounded"
                                style={{
                                  width: `${(data.approved / data.tours) * 100}%`,
                                }}
                              />
                            )}
                            {data.pending > 0 && (
                              <div
                                className="bg-orange-500 rounded"
                                style={{
                                  width: `${(data.pending / data.tours) * 100}%`,
                                }}
                              />
                            )}
                            {data.declined > 0 && (
                              <div
                                className="bg-red-500 rounded"
                                style={{
                                  width: `${(data.declined / data.tours) * 100}%`,
                                }}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {analyticsData.length === 0 && (
                    <div className="text-center py-12">
                      <BarChart3 className="h-16 w-16 mx-auto mb-3 text-gray-300" />
                      <p className="text-gray-500">
                        No upcoming data available
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })()}
      </div>

      <Dialog
        open={!!receiptTour}
        onOpenChange={(open) => {
          if (!open) {
            setReceiptTour(null);
          }
        }}
      >
        <DialogContent
          className="max-h-[90vh] overflow-y-auto bg-slate-100 p-0"
          style={{ width: "94vw", maxWidth: "980px" }}
        >
          {receiptTour && (
            <div className="bg-slate-100 p-4 sm:p-6">
              <div className="mx-auto bg-white px-7 py-7 text-slate-900 shadow-sm ring-1 ring-slate-200">
                <div className="flex items-start justify-between gap-4 border-b-2 border-slate-900 pb-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                      FHIMS Booking Receipt
                    </p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight">
                      Receipt
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Lawazim and venue booking receipt
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="text-right text-sm">
                      <p className="font-bold text-slate-950">
                        {receiptTour.tourCode}
                      </p>
                      <p className="text-slate-600">
                        {new Date().toLocaleDateString("en-GB")}
                      </p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDownloadBookingReceipt(receiptTour)}
                      className="h-10 w-10 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
                      title="Download Receipt PDF"
                    >
                      <Download className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                <div
                  className="grid border-b border-dashed border-slate-300 py-5 text-sm md:grid-cols-[1.2fr_1fr]"
                  style={{ alignItems: "center" }}
                >
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Tour
                    </p>
                    <p className="font-semibold">
                      {receiptTour.tourCode} · {receiptTour.tourName}
                    </p>
                    <p className="mt-1 text-slate-600">
                      {receiptTour.venues?.join(" / ") || "Venue allocation"}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Applicants
                      </p>
                      <p className="font-semibold">
                        {receiptTour.totalApplicants}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Travel Dates
                      </p>
                      <p className="font-semibold whitespace-nowrap">
                        {formatDate(receiptTour.arrivalDate)} -{" "}
                        {formatDate(receiptTour.departureDate)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-[180px_minmax(0,1fr)_120px] gap-x-6 border-b border-slate-900 py-3 text-xs font-bold uppercase tracking-wide text-slate-700">
                  <span>Lawazim</span>
                  <span>Description</span>
                  <span className="text-right">Amount</span>
                </div>

                {Object.values(
                  receiptTour.entries.reduce(
                    (groups, entry) => {
                      const key = entry.lawazimTypeName;

                      if (!groups[key]) {
                        groups[key] = {
                          lawazimTypeName: entry.lawazimTypeName,
                          entries: [],
                        };
                      }

                      groups[key].entries.push(entry);

                      return groups;
                    },
                    {} as Record<
                      string,
                      {
                        lawazimTypeName: string;
                        entries: typeof receiptTour.entries;
                      }
                    >,
                  ),
                ).map((group) => (
                  <div
                    key={group.lawazimTypeName}
                    className="grid grid-cols-1 gap-2 border-b border-slate-200 py-3 text-sm md:grid-cols-[180px_minmax(0,1fr)_120px] md:gap-x-6"
                  >
                    {/* LAWAZIM — SHOW ONLY ONCE */}
                    <div className="font-semibold text-slate-950">
                      {group.lawazimTypeName}
                    </div>

                    {/* ALL DESCRIPTIONS */}
                    <div className="space-y-1 text-slate-700">
                      {group.entries.map((entry) => (
                        <p key={entry.publicId}>{entry.description}</p>
                      ))}
                    </div>

                    {/* INDIVIDUAL AMOUNTS */}
                    <div className="space-y-1 font-semibold text-slate-950 md:text-right">
                      {group.entries.map((entry) => (
                        <p key={entry.publicId}>
                          {formatCurrency(entry.amount)}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="ml-auto mt-5 w-full max-w-sm space-y-2 border-b border-t border-slate-900 py-3 text-sm">
                  <div className="border-slate-300">
                    <div className="flex justify-between gap-4 text-base">
                      <span className="font-bold">Total</span>
                      <span className="font-bold text-[#5B9BD5]">
                        {formatCurrency(
                          receiptTour.entries.reduce(
                            (total, entry) => total + entry.amount,
                            0,
                          ),
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-5 border-t border-dashed border-slate-300 pt-4 text-center text-xs uppercase tracking-[0.22em] text-slate-500">
                  System generated receipt
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!activityLogTour}
        onOpenChange={(open) => {
          if (!open) setActivityLogTour(null);
        }}
      >
        <DialogContent
          className="bg-white max-h-[90vh] overflow-y-auto"
          style={{ width: "96vw", maxWidth: "1680px" }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#5B9BD5]">
              <History className="h-5 w-5" />
              Activity Log
            </DialogTitle>
            <DialogDescription>
              {activityLogTour
                ? `Process history for ${activityLogTour.tourCode} - ${activityLogTour.tourName}`
                : "Tour process history"}
            </DialogDescription>
          </DialogHeader>

          {activityLogTour && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/60 p-3">
                  <p className="text-xs text-muted-foreground">Tour</p>
                  <p className="font-semibold text-[#1f3d5a]">
                    {activityLogTour.tourCode}
                  </p>
                </div>
                <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/60 p-3">
                  <p className="text-xs text-muted-foreground">Dates</p>
                  <p className="font-semibold text-[#1f3d5a]">
                    {formatDate(activityLogTour.arrivalDate ?? "")} -{" "}
                    {formatDate(activityLogTour.departureDate ?? "")}
                  </p>
                </div>
                <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/60 p-3">
                  <p className="text-xs text-muted-foreground">
                    Current Status
                  </p>
                  <Badge
                    variant="outline"
                    className="bg-gray-100 text-gray-700 border-gray-300"
                  >
                    {activityLogTour.statusName}
                  </Badge>
                </div>
              </div>

              <div className="rounded-xl border border-[#5B9BD5]/25 overflow-hidden">
                <div className="hidden lg:grid grid-cols-[220px_minmax(420px,1fr)_220px_180px] gap-3 bg-[#E3F2FD] px-4 py-3 text-sm font-semibold text-[#1f3d5a]">
                  <span>Process</span>
                  <span>Change Details</span>
                  <span>Date & Time</span>
                  <span>Done By</span>
                </div>

                {activityLogs.map((log, index) => (
                  <div
                    key={`${log.process}-${index}`}
                    className="grid grid-cols-1 lg:grid-cols-[220px_minmax(420px,1fr)_220px_180px] gap-2 md:gap-3 px-4 py-4 border-t text-sm"
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#5B9BD5]/10 text-[#5B9BD5] font-semibold text-xs">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-semibold text-[#1f3d5a]">
                          {log.process}
                        </p>
                        <Badge
                          variant="outline"
                          className="mt-1 bg-white text-[#5B9BD5] border-[#5B9BD5]/30"
                        >
                          {log.status}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-muted-foreground md:pt-1">
                      {log.details}
                    </p>
                    <p className="font-medium text-[#1f3d5a] md:pt-1">
                      {formatActivityLogDate(log.dateTime)}
                    </p>
                    <p className="font-medium text-[#1f3d5a] md:pt-1">
                      {log.doneBy}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
